import { AppList } from '@/components/appList/appList.comp';
import { Banner } from '@/components/banner/banner.comp';
import { Layout } from '@/components/layout/layout.comp';
import { MoreApps } from '@/components/moreApps/moreApps.comp';
import { Platforms } from '@/components/platforms/platforms.comp';
import { SearchHero } from '@/components/searchHero/searchHero.comp';
import { getRandomApps } from '@/lib/search';
import { getSearchEngineDataDB } from '@/lib/searchEngineMongodb';
import { useSearch } from '@/lib/useSearch';
import { IPlatform, THit } from '@/types';
import { GetStaticPropsContext } from 'next';
import { useRouter } from 'next/router';

export default function PlatformResultsPage({
	platform,
	platforms,
	categories,
	moreApps,
}: {
	platform: IPlatform;
	platforms: IPlatform[];
	categories: any[];
	moreApps: THit[];
}) {
	const router = useRouter();
	const q = (router.query.q as string) || '';
	const page = Math.max(1, parseInt(router.query.page as string, 10) || 1);
	const search = useSearch({
		q,
		platform: platform.name,
		page,
		skip: !router.isReady,
	});

	return (
		<Layout
			showFooter={true}
			showHeader={true}
			title={`Search results for ${q || platform.displayName}`}
		>
			<SearchHero
				showSearch={false}
				platform={platform}
				total={search.total}
				query={q}
			/>
			<AppList
				categories={categories}
				platforms={platforms}
				activePlatform={platform}
				hits={search.hits}
				page={search.page}
				totalPages={search.totalPages}
				onPageChange={(p) => {
					router.push(
						{ pathname: router.pathname, query: { ...router.query, page: p } },
						undefined,
						{ shallow: true }
					);
				}}
			/>
			<MoreApps appSynonym={platform.appSynonym} hits={moreApps} />
			<Platforms />
			<Banner />
		</Layout>
	);
}

export async function getStaticPaths() {
	try {
		const platforms = await getSearchEngineDataDB<any>('platforms');
		const paths = platforms.map((platform) => ({
			params: {
				platform: platform.name,
			},
		}));

		return { paths, fallback: false };
	} catch (e) {
		console.error(e);
		return { paths: [], fallback: false };
	}
}

export async function getStaticProps({
	params = { platform: '' },
}: GetStaticPropsContext<{ platform: string }>) {
	const wantedPlatform = await getSearchEngineDataDB<IPlatform>('platforms', {
		name: params.platform,
	});

	const categories = await getSearchEngineDataDB<IPlatform>(
		'seocategories',
		{
			platform: params.platform,
		},
		{
			slug: 1,
			displayName: 1,
		}
	);

	const platforms = await getSearchEngineDataDB<IPlatform>(
		'platforms',
		{},
		{
			displayName: 1,
			name: 1,
			appSynonym: 1,
		}
	);

	const moreApps = await getRandomApps({
		platform: params.platform,
		limit: 10,
	});

	return {
		props: { platform: wantedPlatform[0], platforms, categories, moreApps },
	};
}
